import { Component, OnDestroy, OnInit } from '@angular/core';
import { TreeNode } from 'primeng/api';
import { TreeNodeSelectEvent, TreeNodeUnSelectEvent } from 'primeng/tree';
import { StateFilter } from '../../../stores/crud.state';

@Component({
  selector: 'app-base-filter',
  standalone: true,
  imports: [],
  templateUrl: './base-filter.component.html',
  styleUrl: './base-filter.component.scss',
})
export abstract class BaseFilterComponent<T> implements OnInit, OnDestroy {
  abstract readonly store: any;

  filterQuery: StateFilter;

  constructor() {
    this.filterQuery = {
      query: '',
      ids: undefined,
      locked: null,
      published: null,
      category: [],
      tags: [],
      products: [],
    };
  }

  /**
   * The local filter state starts empty, so a filter left over in the store
   * (e.g. from a previous dialog) would silently filter the lists.
   */
  ngOnInit(): void {
    this.store.clearFilter();
  }

  /**
   * Don't leak this filter into other views using the same store.
   */
  ngOnDestroy(): void {
    this.store.clearFilter();
  }

  /**
   * Adds or removes a category and its child categories based on the specified node.
   *
   * @param {TreeNode} node - The tree node representing the category to be added or removed.
   * @param {boolean} [remove] - Optional flag indicating whether to remove the category and its children.
   *                              If true, the category and its children are removed; otherwise, they are added.
   * @return {void}
   */
  private addOrRemoveCategoryAndChildren(
    node: TreeNode,
    remove?: boolean
  ): void {
    if (node.key && !remove) this.addCategoryId(node.key);
    if (node.key && remove) this.removeCategoryId(node.key);
    node.children?.forEach(childNode => {
      if (node.children) {
        this.addOrRemoveCategoryAndChildren(childNode, remove);
      }
    });
  }

  /**
   * Adds a category ID to the filterQuery's category list.
   *
   * @param {string} id - The category ID to be added.
   * @return {void} This method does not return a value.
   */
  addCategoryId(id: string): void {
    this.filterQuery.category.push(id);
  }

  /**
   * Removes a specific category ID from the filterQuery's category list.
   *
   * @param {string} id - The category ID to be removed from the category list.
   * @return {void} This method does not return a value.
   */
  removeCategoryId(id: string): void {
    this.filterQuery.category.splice(this.filterQuery.category.indexOf(id), 1);
  }

  /**
   * Updates the query based on the selected category from the tree node event.
   * Adds or removes the selected category and its child nodes to the query filter
   * and subsequently updates the filter state.
   *
   * @param {TreeNodeSelectEvent} event - The tree node selection event containing the selected node information.
   * @return {void} This method does not return any value.
   */
  addCategoryToQuery(event: TreeNodeSelectEvent): void {
    this.addOrRemoveCategoryAndChildren(event.node);
    this.updateFilter();
  }

  /**
   * Removes a category and its child categories from the query upon unselection.
   *
   * @param {TreeNodeUnSelectEvent} event - The event object containing information about the unselected tree node.
   * @return {void} This method does not return a value.
   */
  removeCategoryFromQuery(event: TreeNodeUnSelectEvent): void {
    this.addOrRemoveCategoryAndChildren(event.node, true);
    this.updateFilter();
  }

  /**
   * Updates the filter by applying the current query and delegating it to the store after a brief delay.
   * This method logs the current filter query and then schedules an update operation for the underlying store.
   *
   * @return {void} This method does not return any value.
   */
  updateFilter(): void {
    setTimeout(() => {
      this.store.updateFilter(this.filterQuery);
    }, 10);
  }
}
